import type { CSSProperties } from "react";

type StaticImport = { src?: string; default?: { src?: string } };

export type NextImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "width" | "height"
> & {
  src: string | StaticImport;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  priority?: boolean;
  sizes?: string;
  unoptimized?: boolean;
};

function resolveSrc(src: NextImageProps["src"]): string {
  if (typeof src === "string") return src;
  if (src && typeof src === "object") {
    if ("src" in src && typeof src.src === "string") return src.src;
    if ("default" in src && src.default && typeof src.default.src === "string")
      return src.default.src;
  }
  return "";
}

/** Root-absolute paths (`/images/...`) must respect Vite `base` (e.g. deployed under a subpath). */
function withBaseIfPublicRoot(url: string): string {
  if (!url.startsWith("/")) return url;
  const base = import.meta.env.BASE_URL;
  return `${base}${url.slice(1)}`;
}

/**
 * Drop-in replacement for `next/image` in Vite (uses native `<img>` + lazy loading).
 */
export default function Image({
  src,
  alt,
  width,
  height,
  fill,
  className,
  style,
  priority,
  sizes: _sizes,
  unoptimized: _unoptimized,
  ...rest
}: NextImageProps) {
  const url = withBaseIfPublicRoot(resolveSrc(src));
  const fillStyle: CSSProperties | undefined = fill
    ? {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }
    : undefined;

  return (
    <img
      src={url}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      className={className}
      style={{ ...fillStyle, ...style }}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      {...rest}
    />
  );
}
